import {useCallback, useMemo, useState} from "react";
import {ScheduleCallModal} from "~/components/scheduleCall/ScheduleCallModal";
import {navigateToScheduleCallRoute, type ScheduleCallNavigateFn} from "~/components/scheduleCall/navigateToScheduleCallRoute";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useEventTemplates} from "~/contexts/workspaceEventTemplatesContext";
import {resolveScheduleCallStrategy} from "~/domainModels/scheduleCall/resolveScheduleCallStrategy";
import {resolveWorkspaceScheduleCallRoute} from "~/domainModels/scheduleCall/resolveWorkspaceScheduleCallRoute";
import type {EventContainerScheduleCallDisableConfig} from "~/domainModels/workspaceModules/eventContainersModule";
import {getScheduleCallDisabledReason} from "~/domainModels/workspaceModules/eventContainerScheduleCallDisable";
import {useAnankeFlags} from "@vesta/ananke/hooks/useAnankeFlags";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EventContainerWithEvents} from "~/utilities/apis/api";

/**
 * View-agnostic schedule-call: navigate to the hosted full-page form when one is
 * configured. The old inline date/time modal is used only when the CTA opts in
 * (`preferInlineModal`) or no hosted schedule route exists.
 */
export function useScheduleCallEngagement({
    navigate,
    workspaceId,
    scheduleRoute,
    onSuccess,
    scheduleCallDisableConfig,
}: {
    navigate: ScheduleCallNavigateFn;
    workspaceId: Uuid;
    /** Override workspace default; falls back to {@link resolveWorkspaceScheduleCallRoute}. */
    scheduleRoute?: string;
    onSuccess?: () => void;
    scheduleCallDisableConfig?: EventContainerScheduleCallDisableConfig;
}) {
    const {showFlag} = useAnankeFlags();
    const {workspace, workspaceCapabilities} = useWorkspace();
    const eventTemplates = useEventTemplates();
    const [scheduleJourney, setScheduleJourney] = useState<EventContainerWithEvents | null>(null);

    const strategy = useMemo(
        () => resolveScheduleCallStrategy(workspace, workspaceCapabilities, eventTemplates),
        [workspace, workspaceCapabilities, eventTemplates],
    );

    const effectiveScheduleRoute = useMemo(() => {
        const override = scheduleRoute?.trim() ?? "";
        if (override !== "") {
            return override;
        }
        return resolveWorkspaceScheduleCallRoute(workspace, workspaceCapabilities);
    }, [scheduleRoute, workspace, workspaceCapabilities]);

    const supportsInlineModal = strategy?.supportsInlineModal === true;
    const hasScheduleAction = supportsInlineModal === true || effectiveScheduleRoute.trim() !== "";

    const openScheduleCall = useCallback(
        (journey: EventContainerWithEvents, options?: {preferInlineModal?: boolean}) => {
            const disabledReason = getScheduleCallDisabledReason(journey, scheduleCallDisableConfig);
            if (disabledReason !== null) {
                showFlag({
                    title: "Cannot schedule call",
                    description: disabledReason,
                    appearance: "warning",
                    isAutoDismiss: true,
                    autoDismissSeconds: 2,
                });
                return;
            }
            const canUseInlineModal = supportsInlineModal === true && strategy !== null;
            const useInlineModal =
                canUseInlineModal === true &&
                (options?.preferInlineModal === true || effectiveScheduleRoute.trim() === "");
            if (useInlineModal === true) {
                setScheduleJourney(journey);
                return;
            }
            if (effectiveScheduleRoute.trim() !== "") {
                navigateToScheduleCallRoute(navigate, effectiveScheduleRoute, workspaceId, journey.id);
            }
        },
        [
            supportsInlineModal,
            strategy,
            effectiveScheduleRoute,
            navigate,
            workspaceId,
            scheduleCallDisableConfig,
            showFlag,
        ],
    );

    const closeScheduleCallModal = useCallback(() => {
        setScheduleJourney(null);
    }, []);

    const scheduleCallModal =
        strategy !== null ? (
            <ScheduleCallModal
                opened={scheduleJourney !== null}
                journey={scheduleJourney}
                strategy={strategy}
                onClose={closeScheduleCallModal}
                onSuccess={onSuccess}
            />
        ) : null;

    return {
        strategy,
        supportsInlineModal,
        hasScheduleAction,
        effectiveScheduleRoute,
        openScheduleCall,
        closeScheduleCallModal,
        scheduleCallModal,
        activeJourneyId: scheduleJourney?.id ?? null,
    };
}
